import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import * as Popover from '@radix-ui/react-popover';
import Icon from '@/components/AppIcon';
import { LoadingState } from '@/components/AsyncStates';
import { RefereeAvailability } from '@/models/tournament';
import type { RefereeScheduleView } from '@/models/tournament';
import { parseApiWallClock } from '@/utils/apiDate';

interface ScheduleCalendarProps {
  entries: RefereeScheduleView[];
  loading: boolean;
  /** true = HR view: no click-to-edit, no save bar */
  readOnly?: boolean;
  /**
   * Called with the date range whenever navigation, view-toggle, jump-to-date,
   * or initial mount occurs. Does NOT need to be a stable reference — the
   * component always calls the latest version via an internal ref.
   */
  onRangeChange: (start: string, end: string) => void;
  onSaveAvailability?: (changes: { date: string; status: RefereeAvailability }[]) => Promise<void>;
}

type CalendarView = 'month' | 'week';

const availabilityColors: Record<RefereeAvailability, { bg: string; text: string; border: string }> = {
  [RefereeAvailability.Available]: {
    bg: 'bg-green-100 dark:bg-green-900/30',
    text: 'text-green-800 dark:text-green-300',
    border: 'border-green-300 dark:border-green-700',
  },
  [RefereeAvailability.Busy]: {
    bg: 'bg-amber-100 dark:bg-amber-900/30',
    text: 'text-amber-800 dark:text-amber-300',
    border: 'border-amber-300 dark:border-amber-700',
  },
  [RefereeAvailability.Unavailable]: {
    bg: 'bg-red-100 dark:bg-red-900/30',
    text: 'text-red-800 dark:text-red-300',
    border: 'border-red-300 dark:border-red-700',
  },
};

const availabilityLabels: Record<RefereeAvailability, string> = {
  [RefereeAvailability.Available]: 'Available',
  [RefereeAvailability.Busy]: 'Busy',
  [RefereeAvailability.Unavailable]: 'Unavailable',
};

function toDateKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function getMonthRange(month: Date): { start: string; end: string } {
  return {
    start: toDateKey(new Date(month.getFullYear(), month.getMonth(), 1)),
    end: toDateKey(new Date(month.getFullYear(), month.getMonth() + 1, 0)),
  };
}

const Legend: React.FC<{ t: (key: string) => string }> = ({ t }) => (
  <div className="flex flex-wrap items-center gap-3 text-xs">
    {([RefereeAvailability.Available, RefereeAvailability.Unavailable] as const).map((s) => (
      <span key={s} className="flex items-center gap-1">
        <span className={`w-3 h-3 rounded-sm ${availabilityColors[s].bg} border ${availabilityColors[s].border}`} />
        <span className="text-muted-foreground">{t(availabilityLabels[s])}</span>
      </span>
    ))}
    <span className="flex items-center gap-1">
      <span className="w-3 h-3 rounded-sm bg-blue-100 dark:bg-blue-900/30 border border-blue-300 dark:border-blue-700" />
      <span className="text-muted-foreground">{t('Match')}</span>
    </span>
  </div>
);

function getWeekRange(anchor: Date): { start: string; end: string; days: Date[] } {
  const mondayOffset = (anchor.getDay() + 6) % 7;
  const monday = new Date(anchor);
  monday.setDate(anchor.getDate() - mondayOffset);
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d;
  });
  return { start: toDateKey(days[0]), end: toDateKey(days[6]), days };
}

const ScheduleCalendar: React.FC<ScheduleCalendarProps> = ({
  entries,
  loading,
  readOnly = false,
  onRangeChange,
  onSaveAvailability,
}) => {
  const { t, i18n } = useTranslation('tournament');
  const [view, setView] = useState<CalendarView>('month');
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [weekAnchor, setWeekAnchor] = useState(() => new Date());
  const [pendingChanges, setPendingChanges] = useState<Map<string, RefereeAvailability>>(new Map());
  const [saving, setSaving] = useState(false);
  const [jumpOpen, setJumpOpen] = useState(false);
  const [matchDetailDay, setMatchDetailDay] = useState<string | null>(null);

  // Always call the latest onRangeChange — no stability contract required from parent.
  const onRangeChangeRef = useRef(onRangeChange);
  useEffect(() => { onRangeChangeRef.current = onRangeChange; });

  // Signal the initial range to the parent on mount.
  useEffect(() => {
    const { start, end } = getMonthRange(currentMonth);
    onRangeChangeRef.current(start, end);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally runs once on mount

  // The backend uses StringEnumConverter globally, so enum values arrive as strings
  // (e.g. "Busy") rather than numbers (1). Coerce them to the numeric enum here.
  const normalizeStatus = (raw: RefereeAvailability | string | undefined): RefereeAvailability => {
    if (raw === undefined || raw === null) return RefereeAvailability.Available;
    // Map legacy "Busy" (1) to "Unavailable" since Busy is no longer a UI concept
    if (typeof raw === 'number') return raw === RefereeAvailability.Busy ? RefereeAvailability.Unavailable : raw as RefereeAvailability;
    const map: Record<string, RefereeAvailability> = {
      Available: RefereeAvailability.Available,
      Busy: RefereeAvailability.Unavailable,
      Unavailable: RefereeAvailability.Unavailable,
    };
    return map[raw as string] ?? RefereeAvailability.Available;
  };

  const scheduleMap = useMemo(() => {
    const map = new Map<string, RefereeScheduleView[]>();
    for (const entry of entries) {
      const key = entry.date.split('T')[0];
      const normalized = { ...entry, status: normalizeStatus(entry.status) };
      const existing = map.get(key);
      if (existing) existing.push(normalized);
      else map.set(key, [normalized]);
    }
    return map;
  }, [entries]);

  const getStatusForDate = (dateKey: string): RefereeAvailability =>
    pendingChanges.get(dateKey) ?? scheduleMap.get(dateKey)?.[0]?.status ?? RefereeAvailability.Available;

  const handleDayClick = (date: Date) => {
    if (readOnly) return;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date < today) return;
    const key = toDateKey(date);
    const dayEntries = scheduleMap.get(key);
    if (dayEntries?.some(e => e.matchId)) return;
    const current = getStatusForDate(key);
    const next =
      current === RefereeAvailability.Available ? RefereeAvailability.Unavailable :
      RefereeAvailability.Available;
    const newPending = new Map(pendingChanges);
    const serverStatus = dayEntries?.[0]?.status ?? RefereeAvailability.Available;
    if (next === serverStatus) newPending.delete(key);
    else newPending.set(key, next);
    setPendingChanges(newPending);
  };

  const handleSave = async () => {
    if (!onSaveAvailability || pendingChanges.size === 0) return;
    setSaving(true);
    try {
      await onSaveAvailability(
        Array.from(pendingChanges.entries()).map(([date, status]) => ({ date, status }))
      );
      setPendingChanges(new Map());
    } catch {
      // onSaveAvailability is responsible for user-facing error display.
      // pendingChanges are preserved so the user can retry.
    } finally {
      setSaving(false);
    }
  };

  const navigateMonth = (offset: number) => {
    const next = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + offset, 1);
    setCurrentMonth(next);
    setPendingChanges(new Map());
    setMatchDetailDay(null);
    const { start, end } = getMonthRange(next);
    onRangeChangeRef.current(start, end);
  };

  const navigateWeek = (offset: number) => {
    const next = new Date(weekAnchor);
    next.setDate(weekAnchor.getDate() + offset * 7);
    setWeekAnchor(next);
    setPendingChanges(new Map());
    const { start, end } = getWeekRange(next);
    onRangeChangeRef.current(start, end);
  };

  // View toggle does NOT reset pendingChanges — user may inspect a week
  // while keeping unsaved availability edits from month view.
  const handleViewChange = (newView: CalendarView) => {
    setView(newView);
    if (newView === 'month') {
      const { start, end } = getMonthRange(currentMonth);
      onRangeChangeRef.current(start, end);
    } else {
      const { start, end } = getWeekRange(weekAnchor);
      onRangeChangeRef.current(start, end);
    }
  };

  const handleJumpToDate = (dateStr: string) => {
    if (!dateStr) return;
    const [y, m, d] = dateStr.split('-').map(Number);
    const target = new Date(y, m - 1, d);
    setPendingChanges(new Map());
    if (view === 'month') {
      const nextMonth = new Date(y, m - 1, 1);
      setCurrentMonth(nextMonth);
      const { start, end } = getMonthRange(nextMonth);
      onRangeChangeRef.current(start, end);
    } else {
      setWeekAnchor(target);
      const { start, end } = getWeekRange(target);
      onRangeChangeRef.current(start, end);
    }
    setJumpOpen(false);
  };

  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startOffset = (firstDay.getDay() + 6) % 7;
    const days: (Date | null)[] = [];
    for (let i = 0; i < startOffset; i++) days.push(null);
    for (let d = 1; d <= lastDay.getDate(); d++) days.push(new Date(year, month, d));
    while (days.length % 7 !== 0) days.push(null);
    return days;
  }, [currentMonth]);

  const weekDays = useMemo(() =>
    Array.from({ length: 7 }, (_, i) =>
      new Date(2024, 0, i + 1).toLocaleDateString(i18n.language, { weekday: 'short' })
    ),
  [i18n.language]);

  const monthLabel = currentMonth.toLocaleDateString(i18n.language, { year: 'numeric', month: 'long' });

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const formatTime = (iso?: string): string | null => {
    if (!iso) return null;
    return (parseApiWallClock(iso) ?? new Date(iso)).toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' });
  };

  const weekData = useMemo(() => getWeekRange(weekAnchor), [weekAnchor]);

  const weekLabel = `${weekData.days[0].toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' })} – ${weekData.days[6].toLocaleDateString(i18n.language, { month: 'short', day: 'numeric', year: 'numeric' })}`;

  return (
    <div className="bg-card border border-border rounded-lg p-6 space-y-4" data-testid="schedule-calendar">
      {/* Header: title + Month/Week toggle */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h3 className="text-sm font-semibold text-foreground flex items-center">
          <Icon name="Calendar" size={16} className="me-2 text-primary" />
          {t('Availability Calendar')}
        </h3>
        <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
          <button
            onClick={() => handleViewChange('month')}
            className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
              view === 'month' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid="calendar-view-month"
          >
            {t('Month')}
          </button>
          <button
            onClick={() => handleViewChange('week')}
            className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
              view === 'week' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid="calendar-view-week"
          >
            {t('Week')}
          </button>
        </div>
      </div>

      {/* Desktop legend */}
      <div className="hidden sm:block"><Legend t={t} /></div>

      {/* Navigation: prev | label + jump-to-date | next */}
      <div className="flex items-center justify-between gap-2">
        <button
          onClick={() => view === 'month' ? navigateMonth(-1) : navigateWeek(-1)}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          data-testid={view === 'month' ? 'calendar-prev-month' : 'calendar-prev'}
        >
          <Icon name="ChevronLeft" size={18} className="rtl:rotate-180" />
        </button>

        <div className="flex items-center gap-2">
          <h4 className="text-base font-semibold text-foreground capitalize">
            {view === 'month' ? monthLabel : weekLabel}
          </h4>

          {/* Jump-to-date popover */}
          <Popover.Root open={jumpOpen} onOpenChange={setJumpOpen}>
            <Popover.Trigger asChild>
              <button
                className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-muted-foreground hover:text-foreground transition-colors"
                title={t('Jump to date')}
                data-testid="calendar-jump-btn"
              >
                <Icon name="CalendarDays" size={15} />
              </button>
            </Popover.Trigger>
            <Popover.Portal>
              <Popover.Content
                sideOffset={8}
                className="z-50 bg-card border border-border rounded-lg shadow-lg p-4 w-64 animate-in fade-in-0 zoom-in-95"
                data-testid="calendar-jump-popover"
              >
                <p className="text-xs font-medium text-foreground mb-2">{t('Jump to date')}</p>
                <input
                  type="date"
                  min={toDateKey(new Date(today.getFullYear() - 5, 0, 1))}
                  className="w-full border border-input rounded-md px-3 py-2 text-sm bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary transition-colors"
                  onChange={(e) => handleJumpToDate(e.target.value)}
                  data-testid="calendar-date-input"
                />
                <Popover.Close asChild>
                  <button
                    className="mt-3 w-full px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground border border-border rounded-md hover:bg-muted transition-colors"
                    data-testid="calendar-jump-close"
                  >
                    {t('Close')}
                  </button>
                </Popover.Close>
                <Popover.Arrow className="fill-border" />
              </Popover.Content>
            </Popover.Portal>
          </Popover.Root>
        </div>

        <button
          onClick={() => view === 'month' ? navigateMonth(1) : navigateWeek(1)}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          data-testid={view === 'month' ? 'calendar-next-month' : 'calendar-next'}
        >
          <Icon name="ChevronRight" size={18} className="rtl:rotate-180" />
        </button>
      </div>

      {loading ? (
        <div className="py-8"><LoadingState title={t('Loading data...')} size="sm" /></div>
      ) : view === 'month' ? (
        <>
          <div className="grid grid-cols-7 gap-1">
            {weekDays.map((day, i) => (
              <div key={i} className="text-center text-xs font-medium text-muted-foreground py-1">{day}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1" data-testid="availability-calendar">
            {calendarDays.map((date, i) => {
              if (!date) return <div key={`empty-${i}`} className="aspect-square" />;
              const dateKey = toDateKey(date);
              const dayEntries = scheduleMap.get(dateKey);
              const status = getStatusForDate(dateKey);
              const matchEntries = dayEntries?.filter(e => e.matchId) ?? [];
              const hasMatch = matchEntries.length > 0;
              const isPast = date < today;
              const isToday = date.getTime() === today.getTime();
              const hasPending = pendingChanges.has(dateKey);
              const colors = availabilityColors[status];
              const firstMatchTime = matchEntries[0]?.matchTime ? formatTime(matchEntries[0].matchTime) : null;
              const isDetailOpen = matchDetailDay === dateKey;
              return (
                <Popover.Root key={dateKey} open={isDetailOpen} onOpenChange={(open) => setMatchDetailDay(open ? dateKey : null)}>
                  <Popover.Trigger asChild>
                    <button
                      type="button"
                      onClick={() => {
                        if (hasMatch) {
                          setMatchDetailDay(isDetailOpen ? null : dateKey);
                        } else {
                          handleDayClick(date);
                        }
                      }}
                      disabled={isPast && !hasMatch}
                      title={
                        hasMatch
                          ? `${matchEntries.length} ${t('match(es)')} — ${t('Click for details')}`
                          : isPast || readOnly ? t(availabilityLabels[status])
                          : t('Click to change availability')
                      }
                      className={`
                        aspect-square rounded-lg border text-xs font-medium flex flex-col items-center justify-center transition-all gap-0.5 relative
                        ${isPast && !hasMatch ? 'opacity-50 cursor-default' : hasMatch ? 'cursor-pointer hover:ring-2 hover:ring-blue-400/50' : readOnly ? 'opacity-50 cursor-default' : 'cursor-pointer hover:ring-2 hover:ring-primary/30'}
                        ${hasMatch ? 'bg-blue-100 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700 text-blue-800 dark:text-blue-300' : `${colors.bg} ${colors.border} ${colors.text}`}
                        ${isToday ? 'ring-2 ring-primary/50' : ''}
                        ${hasPending ? 'ring-2 ring-offset-1 ring-primary' : ''}
                      `}
                      data-testid={`calendar-day-${dateKey}`}
                    >
                      <span>{date.getDate()}</span>
                      {hasMatch && (
                        <span className="text-[8px] leading-none font-semibold truncate max-w-full px-0.5">
                          {firstMatchTime ?? <Icon name="Swords" size={9} />}
                        </span>
                      )}
                      {matchEntries.length > 1 && (
                        <span className="absolute top-0.5 end-0.5 w-3.5 h-3.5 rounded-full bg-blue-600 text-white text-[8px] font-bold flex items-center justify-center">
                          {matchEntries.length}
                        </span>
                      )}
                    </button>
                  </Popover.Trigger>
                  {hasMatch && (
                    <Popover.Portal>
                      <Popover.Content
                        sideOffset={6}
                        className="z-50 bg-card border border-border rounded-lg shadow-lg p-3 w-64 animate-in fade-in-0 zoom-in-95 max-h-60 overflow-y-auto"
                        data-testid={`match-detail-popup-${dateKey}`}
                      >
                        <p className="text-xs font-semibold text-foreground mb-2">
                          {date.toLocaleDateString(i18n.language, { weekday: 'short', month: 'short', day: 'numeric' })}
                        </p>
                        <div className="space-y-2">
                          {matchEntries.map((entry, idx) => (
                            <div key={entry.matchId ?? idx} className="bg-blue-50 dark:bg-blue-900/20 rounded-md p-2 text-xs space-y-1">
                              <div className="flex items-center gap-1 font-medium text-blue-900 dark:text-blue-200">
                                <Icon name="Swords" size={11} />
                                <span>{entry.matchDescription}</span>
                              </div>
                              {entry.matchTime && (
                                <div className="flex items-center gap-1 text-muted-foreground">
                                  <Icon name="Clock" size={11} />
                                  <span>{formatTime(entry.matchTime)}</span>
                                </div>
                              )}
                              {entry.matchLocation && (
                                <div className="flex items-center gap-1 text-muted-foreground">
                                  <Icon name="MapPin" size={11} />
                                  <span>{entry.matchLocation}</span>
                                </div>
                              )}
                              {entry.matchStatus && (
                                <div className="flex items-center gap-1 text-muted-foreground">
                                  <Icon name="Info" size={11} />
                                  <span>{t(entry.matchStatus)}</span>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                        <Popover.Arrow className="fill-border" />
                      </Popover.Content>
                    </Popover.Portal>
                  )}
                </Popover.Root>
              );
            })}
          </div>
        </>
      ) : (
        /* Week view — overflow-x-auto + min-w ensures readable columns in any container */
        <div className="overflow-x-auto">
          <div className="min-w-[480px]">
            <div className="grid grid-cols-7 gap-1 mb-1">
              {weekData.days.map((date) => {
                const isToday = date.getTime() === today.getTime();
                return (
                  <div key={`wh-${toDateKey(date)}`} className="text-center py-1">
                    <div className={`text-xs font-medium ${isToday ? 'text-primary' : 'text-muted-foreground'}`}>
                      {weekDays[(date.getDay() + 6) % 7]}
                    </div>
                    <div className={`text-base font-bold ${isToday ? 'text-primary' : 'text-foreground'}`}>
                      {date.getDate()}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="grid grid-cols-7 gap-1" data-testid="week-calendar">
              {weekData.days.map((date) => {
                const dateKey = toDateKey(date);
                const dayEntries = scheduleMap.get(dateKey);
                const status = getStatusForDate(dateKey);
                const matchEntries = dayEntries?.filter(e => e.matchId) ?? [];
                const hasMatch = matchEntries.length > 0;
                const isPast = date < today;
                const isToday = date.getTime() === today.getTime();
                const hasPending = pendingChanges.has(dateKey);
                const colors = availabilityColors[status];
                return (
                  <button
                    key={`wc-${dateKey}`}
                    type="button"
                    onClick={() => handleDayClick(date)}
                    disabled={isPast || hasMatch || readOnly}
                    className={`
                      min-h-[100px] rounded-lg border p-1.5 flex flex-col gap-1 transition-all text-start
                      ${hasMatch ? 'bg-blue-100 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700' : `${colors.bg} ${colors.border}`}
                      ${!isPast && !hasMatch && !readOnly ? 'cursor-pointer hover:ring-2 hover:ring-primary/30' : 'cursor-default'}
                      ${isToday ? 'ring-2 ring-primary/50' : ''}
                      ${hasPending ? 'ring-2 ring-offset-1 ring-primary' : ''}
                      ${isPast ? 'opacity-50' : ''}
                    `}
                    data-testid={`week-day-${dateKey}`}
                  >
                    {hasMatch ? (
                      matchEntries.map((entry, idx) => (
                        <div key={entry.matchId ?? idx} className="bg-blue-200 dark:bg-blue-800/50 rounded-md p-1 text-[10px] text-blue-900 dark:text-blue-200 leading-tight">
                          <div className="flex items-center gap-0.5 mb-0.5 font-medium">
                            <Icon name="Swords" size={9} />
                            <span>{entry.matchTime ? formatTime(entry.matchTime) : ''}</span>
                          </div>
                          <div className="truncate">{entry.matchDescription}</div>
                          {entry.matchLocation && (
                            <div className="flex items-center gap-0.5 text-blue-700 dark:text-blue-300 opacity-70 mt-0.5">
                              <Icon name="MapPin" size={8} />
                              <span className="truncate">{entry.matchLocation}</span>
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <span className={`text-[10px] font-medium ${colors.text} opacity-70`}>
                        {t(availabilityLabels[status])}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Mobile legend */}
      <div className="sm:hidden"><Legend t={t} /></div>

      {!readOnly && (
        <p className="text-xs text-muted-foreground">
          {t('Click on a future date to toggle between Available and Unavailable. Dates with matches cannot be changed.')}
        </p>
      )}

      {/* Save bar */}
      {!readOnly && pendingChanges.size > 0 && (
        <div className="flex items-center justify-between bg-primary/5 border border-primary/20 rounded-lg p-3">
          <p className="text-sm text-foreground">
            {t('{{count}} day(s) changed', { count: pendingChanges.size })}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPendingChanges(new Map())}
              className="px-3 py-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg"
              data-testid="cancel-schedule-changes"
            >
              {t('Discard')}
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center px-4 py-1.5 rounded-lg text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm disabled:opacity-50"
              data-testid="save-schedule-btn"
            >
              {saving ? (
                <><Icon name="Loader2" size={14} className="me-1.5 animate-spin" />{t('Saving...')}</>
              ) : (
                <><Icon name="Save" size={14} className="me-1.5" />{t('Save Availability')}</>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScheduleCalendar;
